import type { MouseEvent } from 'react';
import type { Swiper as SwiperClass } from 'swiper';
import type { BannerDtoType } from '@/queries/banners/banners.schema';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Link } from '@/i18n/routing';
import openModal from '@/modals/utils/openModal';
import { cn } from '@/utils/cn';
import { getRootModalLink, sortBannersByOrder } from '../lobbyBanner.utils';
import { BetterPicture } from './Picture';
import 'swiper/css';

export const BANNER_SHAPE_CLASS
  = 'w-full aspect-[1128/307] max-sm:aspect-[328/220] max-sm:rounded-[7cqw] rounded-[2.4cqw]';

const AUTOPLAY_DELAY_MS = 5000;
const ENABLE_PAGINATION_PROGRESS = false;
const PAGINATION_DOT_WIDTH = '0.3125rem';
const PAGINATION_ACTIVE_WIDTH = 'clamp(1.4375rem, 6vw, 2.5rem)';
const PAGINATION_GAP = '0.3125rem';

const BASE_BANNER_PICTURE_PROPS = {
  height: 220,
  width: 1064,
  quality: 100,
  priority: true,
  className: BANNER_SHAPE_CLASS,
  fetchPriority: 'high' as const,
};

interface DefaultBannersProps {
  banners: BannerDtoType[];
}

export default function DefaultBanners({ banners }: DefaultBannersProps) {
  const [mainSwiper, setMainSwiper] = useState<SwiperClass | null>(null);

  const [activeIndex, setActiveIndex] = useState(0);
  const [activeProgress, setActiveProgress] = useState(0);

  const [isMounted, setIsMounted] = useState(false);

  const slides = useMemo(() => {
    const sortedBanners = sortBannersByOrder(banners);

    const readySlideList = sortedBanners.map((banner) => {
      const desktopAsset = banner?.assets?.find(asset => asset.appearance === 'desktop');
      const mobileAsset = banner?.assets?.find(asset => asset.appearance === 'mobile');

      const availableAsset = desktopAsset ?? mobileAsset;
      const redirectUrl = banner?.presentation?.redirectUrl ?? '';

      if (!availableAsset) {
        // Skip banners without any available asset
        return null;
      }

      const pictureProps = {
        src: availableAsset?.key ?? availableAsset?.path ?? '',
        srcMobile: mobileAsset?.key ?? mobileAsset?.path ?? '',
        alt: availableAsset?.meta?.alt ?? banner.name ?? '',
        ...BASE_BANNER_PICTURE_PROPS,
      };

      return {
        id: banner.id,
        redirectUrl,
        pictureProps,
      };
    });

    return readySlideList.filter((slide): slide is NonNullable<typeof slide> => slide !== null);
  }, [banners]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const goTo = useCallback((index: number) => {
    if (!mainSwiper) {
      return;
    }

    mainSwiper.slideTo(index);
  }, [mainSwiper]);

  const handleBannerClick = useCallback((event: MouseEvent<HTMLAnchorElement>, redirectUrl: string) => {
    // Keep browser-native link affordances intact: opening in a new tab/window
    // should still use the original href instead of our client-side modal flow.
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    const modalLink = getRootModalLink(redirectUrl);

    if (!modalLink) {
      return;
    }

    // Modal banner URLs are deep links to UI state (`/?m=...`). Opening them
    // through the modal helper avoids a slow Next.js navigation back to `/`.
    event.preventDefault();
    openModal(modalLink.modalName, modalLink.modalData as never);
  }, []);

  const onSlideChange = useCallback((swiper: SwiperClass) => {
    setActiveIndex(swiper.realIndex ?? swiper.activeIndex);
    if (ENABLE_PAGINATION_PROGRESS) {
      setActiveProgress(0);
    }
  }, []);

  const onAutoplayTimeLeft = useCallback((_: SwiperClass, timeLeftMs: number) => {
    if (!ENABLE_PAGINATION_PROGRESS) {
      return;
    }

    const p = Math.min(1, Math.max(0, (AUTOPLAY_DELAY_MS - timeLeftMs) / AUTOPLAY_DELAY_MS));
    setActiveProgress(p);
  }, []);

  if (slides.length === 0) {
    return null;
  }

  return (
    <div className="-ml-4 -mr-4">
      <Swiper
        onSwiper={setMainSwiper}
        breakpoints={{
          1024: {
            effect: 'fade',
          },
        }}
        autoplay={{
          delay: AUTOPLAY_DELAY_MS,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }}
        onAutoplayTimeLeft={onAutoplayTimeLeft}
        onSlideChange={onSlideChange}
        modules={[Autoplay]}
      >
        {slides.map(slide => (
          <SwiperSlide key={slide.id} className="backface-hidden">
            <div data-id={slide.id} className="backface-hidden px-4">
              {slide.redirectUrl
                ? (
                    <Link href={slide.redirectUrl} onClick={event => handleBannerClick(event, slide.redirectUrl)}>
                      <BetterPicture {...slide.pictureProps} />
                    </Link>
                  )
                : (
                    <BetterPicture {...slide.pictureProps} />
                  )}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      <div
        className={cn(
          'px-4 mb-1 flex justify-center transition-opacity duration-200',
          !isMounted && 'opacity-0 pointer-events-none',
        )}
      >
        <div
          className="relative my-3 h-1.25"
          style={{
            width: `calc(${slides.length} * ${PAGINATION_DOT_WIDTH} + ${Math.max(slides.length - 1, 0)} * ${PAGINATION_GAP} + (${PAGINATION_ACTIVE_WIDTH} - ${PAGINATION_DOT_WIDTH}))`,
          }}
        >
          {slides.map((slide, idx) => {
            const isActive = idx === activeIndex;
            const widthPct = ENABLE_PAGINATION_PROGRESS && isActive
              ? Math.round(activeProgress * 100)
              : 0;
            const offsetAfterActive = idx > activeIndex ? ` + (${PAGINATION_ACTIVE_WIDTH} - ${PAGINATION_DOT_WIDTH})` : '';

            return (
              <button
                key={`${slide.id}-progress`}
                type="button"
                aria-label={`Go to banner ${idx + 1}`}
                aria-current={isActive ? 'true' : undefined}
                className={cn('absolute top-0 block h-1.25 rounded-full bg-c400 overflow-hidden transition-[left,width,background-color,opacity] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none', {
                  'bg-c400 opacity-100': isActive,
                  'bg-main-800 opacity-70 hover:opacity-100': !isActive,
                })}
                onClick={() => goTo(idx)}
                style={{
                  left: `calc(${idx} * (${PAGINATION_DOT_WIDTH} + ${PAGINATION_GAP})${offsetAfterActive})`,
                  width: isActive ? PAGINATION_ACTIVE_WIDTH : PAGINATION_DOT_WIDTH,
                }}
              >
                {ENABLE_PAGINATION_PROGRESS
                  ? (
                      <div
                        className="h-full bg-white/80 transition-[width] duration-100"
                        style={{ width: `${widthPct}%` }}
                      />
                    )
                  : null}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
